/**
 * tn-luxury-theme-2 — AJAX add to cart + sidebar cart drawer.
 * - One delegated listener per event type (safe for dynamically added product cards, no duplicates).
 * - Uses Shopify's /cart/add.js and /cart/change.js and the Section Rendering API ("cart-drawer").
 */
(function () {
  'use strict';

  if (window.__tnCartDrawerLoaded) return;
  window.__tnCartDrawerLoaded = true;

  var SECTION_ID = 'cart-drawer';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ERR_GENERIC = 'Sorry, something went wrong. Please try again.';
  var ERR_NETWORK = 'Network error. Please check your connection and try again.';

  var isUpdating = false;
  var lastFocused = null;

  function getDrawer() { return document.getElementById('CartDrawer'); }
  function getPanel() {
    var d = getDrawer();
    return d ? d.querySelector('.cart-drawer__panel') : null;
  }

  /* ---------- Open / close ---------- */
  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return false;
    if (drawer.classList.contains('is-open')) return true;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-open');
    setTimeout(function () {
      var panel = getPanel();
      if (panel) panel.focus();
    }, 50);
    return true;
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-open');
    if (lastFocused && document.contains(lastFocused) && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  /* ---------- Rendering ---------- */
  function updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count < 1;
    });
  }

  function applySection(html) {
    var drawer = getDrawer();
    var panel = getPanel();
    if (!drawer || !panel || !html) return false;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.getElementById('CartDrawer');
    var freshPanel = fresh && fresh.querySelector('.cart-drawer__panel');
    if (!freshPanel) return false;
    panel.innerHTML = freshPanel.innerHTML;
    var count = parseInt(fresh.getAttribute('data-item-count'), 10) || 0;
    drawer.setAttribute('data-item-count', count);
    updateCount(count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: count } }));
    return true;
  }

  async function refreshDrawer() {
    var res = await fetch(ROOT + '?sections=' + SECTION_ID + '&t=' + Date.now(), {
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) throw new Error('Could not load cart');
    var json = await res.json();
    if (!applySection(json[SECTION_ID])) throw new Error('Could not render cart');
  }

  function showNotice(message) {
    var el = document.getElementById('CartDrawerNotice');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function restoreFocus(info) {
    var panel = getPanel();
    if (!panel) return;
    var el = null;
    if (info) {
      el = panel.querySelector('[data-cart-line="' + info.line + '"][data-cart-action="' + info.action + '"]:not([disabled])');
    }
    (el || panel.querySelector('.cart-drawer__close') || panel).focus();
  }

  /* ---------- Add to cart ---------- */
  function setLoading(button, loading) {
    if (!button) return;
    if (loading) {
      button.dataset.label = button.textContent;
      button.textContent = 'Adding\u2026';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      if (button.dataset.label !== undefined) button.textContent = button.dataset.label;
      delete button.dataset.label;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  function clearFormError(form) {
    form.querySelectorAll('.tn-form-error').forEach(function (el) { el.remove(); });
  }

  function showFormError(form, message) {
    clearFormError(form);
    var p = document.createElement('p');
    p.className = 'tn-form-error';
    p.setAttribute('role', 'alert');
    p.textContent = message;
    form.appendChild(p);
  }

  async function addToCart(form, submitter) {
    var button = submitter || form.querySelector('[type="submit"]');
    form.dataset.cartBusy = 'true';
    clearFormError(form);
    setLoading(button, true);

    try {
      var formData = new FormData(form);
      if (!formData.get('id')) {
        throw { userMessage: 'Please select an option before adding to cart.' };
      }
      formData.append('sections', SECTION_ID);

      var res = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
      });
      var data = await res.json();

      if (!res.ok || data.status) {
        throw { userMessage: data.description || data.message || ERR_GENERIC };
      }

      var rendered = data.sections && applySection(data.sections[SECTION_ID]);
      if (!rendered) await refreshDrawer();
      openDrawer();
    } catch (err) {
      var message = (err && err.userMessage) ||
        (err instanceof TypeError ? ERR_NETWORK : ERR_GENERIC);
      showFormError(form, message);
    } finally {
      setLoading(button, false);
      delete form.dataset.cartBusy;
    }
  }

  /* ---------- Change quantity / remove ---------- */
  async function changeLine(line, quantity, focusInfo) {
    if (isUpdating) return;
    isUpdating = true;
    var drawer = getDrawer();
    if (drawer) drawer.classList.add('is-updating');
    var message = '';

    try {
      var res = await fetch(ROOT + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity, sections: SECTION_ID })
      });
      var data = await res.json();

      if (!res.ok || data.status) {
        message = data.description || data.message || ERR_GENERIC;
        await refreshDrawer();
      } else if (!(data.sections && applySection(data.sections[SECTION_ID]))) {
        await refreshDrawer();
      }
    } catch (err) {
      message = err instanceof TypeError ? ERR_NETWORK : ERR_GENERIC;
      try { await refreshDrawer(); } catch (e) { /* keep current markup */ }
    } finally {
      isUpdating = false;
      if (drawer) drawer.classList.remove('is-updating');
    }

    restoreFocus(focusInfo);
    if (message) showNotice(message);
  }

  /* ---------- Event delegation (registered once) ---------- */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add\/?(\?.*)?$/.test(form.getAttribute('action') || '')) return;
    if (!getDrawer()) return; // no drawer: fall back to the normal form post
    event.preventDefault();
    if (form.dataset.cartBusy === 'true') return;
    addToCart(form, event.submitter);
  });

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-cart-toggle]');
    if (toggle) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!getDrawer()) return;
      event.preventDefault();
      openDrawer();
      refreshDrawer().catch(function () { /* server-rendered markup is still shown */ });
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    var control = event.target.closest('[data-cart-qty]');
    if (control && !control.disabled && getDrawer() && getDrawer().contains(control)) {
      event.preventDefault();
      changeLine(
        parseInt(control.getAttribute('data-cart-line'), 10),
        parseInt(control.getAttribute('data-cart-qty'), 10),
        { line: control.getAttribute('data-cart-line'), action: control.getAttribute('data-cart-action') }
      );
    }
  });

  document.addEventListener('keydown', function (event) {
    var drawer = getDrawer();
    if (!drawer || !drawer.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }

    if (event.key === 'Tab') {
      var panel = getPanel();
      var focusable = panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) { event.preventDefault(); panel.focus(); return; }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // Back/forward cache restores can show stale cart data; resync with Shopify.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshDrawer().catch(function () {});
  });
})();